import React from 'react';
import { useRouter } from 'next/router';

import { useMutation } from 'react-query';

import { DocumentDownload, ArrowRight2 } from 'iconsax-react';

import { DownloadToolkit, PurchaseToolkit } from 'services/toolkits';
import { getErrorText } from 'utils/geterrortext';
import { isUserLoggedIn } from 'utils/authentication';

import ActionButton from '@core/components/actionbutton/ActionButton';

import classes from './ToolkitAccessCard.module.scss';

const NEED_SUBSCRIPTION_STATUS = 436;
const NEED_UPGRADE_STATUS = 438;

const ToolkitAccessCard = ({ toolkit, toast }) => {
    const router = useRouter();

    const downloadMutation = useMutation(
        ['toolkit-download', toolkit?.id],
        () => DownloadToolkit(toolkit.id),
        {
            onSuccess: (response) => {
                const downloadUrl = response?.data?.download_url;
                if (downloadUrl) {
                    window.open(downloadUrl, '_blank', 'noopener,noreferrer');
                }
            },
            onError: (error) => {
                const status = error?.response?.status;
                if (status === 401) {
                    router.push(`/login?next=/toolkits/${toolkit.id}/${toolkit.slug}`);
                    return;
                }
                if (status === NEED_SUBSCRIPTION_STATUS) {
                    toast?.(
                        error?.response?.data?.data || 'This toolkit is available only to Pro subscribers. Please subscribe to download it.',
                        'alert'
                    );
                    return;
                }
                if (status === NEED_UPGRADE_STATUS) {
                    toast?.(
                        error?.response?.data?.data || 'This toolkit is available only to Pro subscribers. Please upgrade your subscription to download it.',
                        'alert'
                    );
                    return;
                }
                toast?.(getErrorText(error), 'alert');
            },
        }
    );

    const purchaseMutation = useMutation(
        ['toolkit-purchase', toolkit?.id],
        () => PurchaseToolkit(toolkit.id),
        {
            onSuccess: (response) => {
                // eslint-disable-next-line no-console
                console.log('[toolkit-purchase] response', response?.data);
                const payload = response?.data;
                const checkoutUrl = payload?.link;
                if (typeof checkoutUrl === 'string' && checkoutUrl.startsWith('http')) {
                    window.location.href = checkoutUrl;
                } else {
                    toast?.('Could not start checkout. Please try again.', 'alert');
                }
            },
            onError: (error) => {
                if (error?.response?.status === 401) {
                    router.push(`/login?next=/toolkits/${toolkit.id}/${toolkit.slug}`);
                    return;
                }
                toast?.(getErrorText(error), 'alert');
            },
        }
    );

    const handleDownload = () => {
        if (!isUserLoggedIn()) {
            router.push(`/login?next=/toolkits/${toolkit.id}/${toolkit.slug}`);
            return;
        }
        downloadMutation.mutate();
    };

    const handleBuy = () => {
        if (!isUserLoggedIn()) {
            router.push(`/login?next=/toolkits/${toolkit.id}/${toolkit.slug}`);
            return;
        }
        purchaseMutation.mutate();
    };

    if (toolkit?.has_access) {
        return (
            <div className={classes['card']}>
                <div className={classes['title']}>
                    <h3 className={classes['success']}>You have access to this tool</h3>
                </div>
                <div className={classes['divider']} />

                <button
                    type="button"
                    className={classes['download-btn']}
                    onClick={handleDownload}
                    disabled={downloadMutation.isLoading}
                >
                    <span className={classes['download-left']}>
                        <DocumentDownload size={22} />
                        <span>
                            {downloadMutation.isLoading
                                ? 'Preparing...'
                                : `Download Template${toolkit?.file_type ? ` (${toolkit.file_type.toLowerCase()})` : ''}`}
                        </span>
                    </span>
                    <ArrowRight2 size={20} />
                </button>

                {(toolkit?.file_size || toolkit?.file_type) && (
                    <div className={classes['file-meta']}>
                        {toolkit?.file_size && <span>File size: {toolkit.file_size}</span>}
                        {toolkit?.file_type && (
                            <>
                                <span className={classes['dot']}>•</span>
                                <span>File Type: {toolkit.file_type}</span>
                            </>
                        )}
                        <span className={classes['dot']}>•</span>
                        <span>File Format: Editable</span>
                    </div>
                )}
            </div>
        );
    }

    const hasPurchaseOption = !!toolkit?.purchase_option;
    const showSubscribe = toolkit?.subscription_action === 'subscribe';
    const showUpgrade = toolkit?.subscription_action === 'upgrade';
    const showSubscription = showSubscribe || showUpgrade;
    const showOr = hasPurchaseOption && showSubscription;

    const price = toolkit?.purchase_option?.price ?? toolkit?.purchase_option?.amount_due;
    const symbol = toolkit?.purchase_option?.currency?.symbol || '';
    const formattedPrice = price !== undefined && price !== null ? `${symbol}${price}` : '';

    return (
        <div className={classes['card']}>
            <div className={classes['title']}>
                <h3 className={classes['highlight']}>Start Using this tool:</h3>
            </div>
            <div className={classes['divider']} />

            {hasPurchaseOption && (
                <div className={`${classes['option-row']} ${classes['purchase']}`}>
                    <div className={classes['option-text']}>
                        <h4>Buy this tool</h4>
                        <span>One-time payment</span>
                    </div>
                    <ActionButton
                        type="button"
                        onClick={handleBuy}
                        loading={purchaseMutation.isLoading}
                        sx={{ width: 'auto', minWidth: '140px', height: '46px', fontSize: '16px' }}
                    >
                        {formattedPrice ? `Buy for ${formattedPrice}` : 'Buy now'}
                    </ActionButton>
                </div>
            )}

            {showOr && (
                <div className={classes['or']}>
                    <span>Or</span>
                </div>
            )}

            {showSubscription && (
                <div className={`${classes['option-row']} ${classes['subscription']}`}>
                    <div className={classes['option-text']}>
                        <h4>
                            {showUpgrade ? 'Upgrade to access' : 'Included in Pharmuni Plus'}
                        </h4>
                        <span>Access to tools and all Pharmuni resources</span>
                    </div>
                    <button
                        type="button"
                        className={classes['link-button']}
                        onClick={() => router.push(showUpgrade ? '/profile/my-account' : '/checkout?productId=3')}
                    >
                        {showUpgrade ? 'Upgrade Plan' : 'See Plans'}
                        <ArrowRight2 size={20} />
                    </button>
                </div>
            )}
        </div>
    );
};

export default ToolkitAccessCard;
